import React, { useState } from "react";
import useAxios from "../utils/useAxios";
import { ThreeDots } from 'react-loading-icons'

import { CheckIcon, CloudArrowDownIcon } from "@heroicons/react/20/solid";
import Swal from "sweetalert2";

const TableResultsComponent = ({ results }) => {
  const api = useAxios();

  const [isLoadingDownload, setIsLoadingDownload] = useState(false);

  const selectAllDocuments = () => {
    const checkboxes = document.querySelectorAll(
      'input[type="checkbox"]:not([disabled])'
    );
    const firstCheckbox = checkboxes[0];
    const shouldCheck = !firstCheckbox?.checked;
    
    checkboxes.forEach((checkbox) => {
      checkbox.checked = shouldCheck;
    });
  };

  const handleFileDownload = async (fileUrl, fileType, documentType, documentId, documentNumber) => {
    if (documentType === 'Comprobante de retención') {
      try {
        const response = await api.get(fileUrl);
        
        // Decodificar base64 a datos binarios
        const decodedData = atob(response.data);
        const byteArray = new Uint8Array(decodedData.length);
        for (let i = 0; i < decodedData.length; i++) {
          byteArray[i] = decodedData.charCodeAt(i);
        }
        
        const blob = new Blob([byteArray], { 
          type: fileType === 'xml' ? 'application/xml' : 'application/pdf' 
        });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${documentNumber}.${fileType}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        Swal.fire({
          title: "Error",
          text: `Error al descargar el archivo: ${error.response?.data?.error || error.message}`,
          icon: "error",
          toast: true,
          timer: 4000,
          position: "top-right",
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } else {
      window.open(fileUrl, '_blank');
    }
  };

  const downloadSelectedDocuments = async () => {
    setIsLoadingDownload(true);
    const checkboxes = document.querySelectorAll(
      'input[type="checkbox"]:not([disabled])'
    );
    let selectedDocuments = [];
    checkboxes.forEach((checkbox) => {
      if (checkbox.checked) {
        // Extraer el ID real removiendo el prefijo 'mobile-' si existe
        const docId = checkbox.id.replace('mobile-', '');
        if (!selectedDocuments.includes(docId)) {
          selectedDocuments.push(docId);
        }
      }
    });

    let response;

    try {
      response = await api.post("/electronic-documents/download/", {
        document_ids: selectedDocuments,
      });
      setIsLoadingDownload(false);
    } catch (error) {
      Swal.fire({
        title: "Oops...",
        text: `Ocurrió un error al descargar los comprobantes: ${error.response.data.error || error}`,
        icon: "error",
        toast: true,
        timer: 6000,
        position: "top-right",
        timerProgressBar: true,
        showConfirmButton: false,
      });
      setIsLoadingDownload(false);
      return;
    }

    const decodedData = atob(response.data.zip);

    const byteArray = new Uint8Array(decodedData.length);
    for (let i = 0; i < decodedData.length; i++) {
      byteArray[i] = decodedData.charCodeAt(i);
    }

    const blob = new Blob([byteArray], { type: "application/zip" });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    const currentDate = new Date();
    const formattedDate = currentDate.toISOString().slice(0, 10);
    const fileName = `nosolotecsa_${formattedDate}.zip`;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
  };

  return (
    <div className="bg-transparent mt-6 mx-4 sm:mx-5 lg:mx-5 rounded-lg p-4 sm:p-6 mb-10">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="min-w-0 flex-1">
          <h2 className="font-extralight text-lg sm:text-xl text-gray-900">
            Comprobantes generados
          </h2>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:gap-3">
          <button
            type="button"
            onClick={selectAllDocuments}
            className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 w-full sm:w-auto"
          >
            <CheckIcon
              className="-ml-0.5 mr-1.5 h-4 w-4 sm:h-5 sm:w-5"
              aria-hidden="true"
            />
            <span className="hidden sm:inline">Seleccionar todos</span>
            <span className="sm:hidden">Seleccionar</span>
          </button>

          <button
            type="button"
            disabled={isLoadingDownload}
            onClick={downloadSelectedDocuments}
            className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CloudArrowDownIcon
              className={!isLoadingDownload ? "-ml-0.5 mr-1.5 h-4 w-4 sm:h-5 sm:w-5" : "hidden"}
              aria-hidden="true"
            />
            <ThreeDots 
              className={isLoadingDownload ? "-ml-0.5 mr-1.5 h-4 w-4 sm:h-5 sm:w-5" : "hidden"}
              aria-hidden="true"
            />
            <span className="hidden sm:inline">Descargar seleccionados</span>
            <span className="sm:hidden">Descargar</span>
          </button>
        </div>
      </div>

      {/* Vista de tabla para pantallas medianas y grandes */}
      <div className="hidden md:block">
        <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
          <table className="w-full text-sm text-left text-gray-500">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50">
              <tr>
                <th scope="col" className="p-4 w-8"></th>
                <th scope="col" className="px-4 py-3 min-w-[120px]">Identificación</th>
                <th scope="col" className="px-4 py-3 min-w-[150px]">Empresa</th>
                <th scope="col" className="px-4 py-3 min-w-[140px]">Comprobante</th>
                <th scope="col" className="px-4 py-3 min-w-[100px]">Tipo</th>
                <th scope="col" className="px-4 py-3 min-w-[120px]">Fecha Emisión</th>
                <th scope="col" className="px-4 py-3 min-w-[100px]">Valor</th>
                <th scope="col" className="px-4 py-3 w-16">XML</th>
                <th scope="col" className="px-4 py-3 w-16">RIDE</th>
              </tr>
            </thead>
            <tbody>
              {results.map((result) => (
                <tr key={result.id} className="bg-white border-b hover:bg-gray-50">
                  <td className="w-4 p-4">
                    <div className="flex items-center">
                      <input
                        id={result.id}
                        type="checkbox"
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor={result.id} className="sr-only">checkbox</label>
                    </div>
                  </td>
                  <td className="px-4 py-4 font-medium text-gray-900">{result.vat}</td>
                  <td className="px-4 py-4 max-w-[200px] truncate" title={result.company_name}>
                    {result.company_name}
                  </td>
                  <td className="px-4 py-4">{result.document_number}</td>
                  <td className="px-4 py-4 text-xs">{result.get_document_type_display}</td>
                  <td className="px-4 py-4 text-xs">
                    {new Date(result.emission_date).toLocaleDateString("es-ES", {
                      day: "2-digit",
                      month: "2-digit", 
                      year: "numeric",
                    })}
                  </td>
                  <td className="px-4 py-4 font-semibold">$ {result.amount.toFixed(2)}</td>
                  <td className="px-4 py-4">
                    <button
                      className="w-8 h-8 hover:opacity-80 transition-opacity"
                      onClick={() => handleFileDownload(
                        result.file_xml_url, 'xml', result.get_document_type_display, 
                        result.id, result.document_number
                      )}
                      title="Descargar XML"
                    >
                      <img src={require("../assets/img/xml.png")} alt="XML" className="w-full h-full object-contain" />
                    </button>
                  </td>
                  <td className="px-4 py-4">
                    <button
                      className="w-8 h-8 hover:opacity-80 transition-opacity"
                      onClick={() => handleFileDownload(
                        result.file_ride_url, 'pdf', result.get_document_type_display, 
                        result.id, result.document_number
                      )}
                      title="Descargar PDF"
                    >
                      <img src={require("../assets/img/pdf.png")} alt="PDF" className="w-full h-full object-contain" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Vista de tarjetas para móviles y tablets */}
      <div className="md:hidden space-y-4">
        {results.map((result) => (
          <div key={result.id} className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <input
                  id={`mobile-${result.id}`}
                  type="checkbox"
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">{result.document_number}</h3>
                  <p className="text-xs text-gray-500">{result.get_document_type_display}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  className="w-8 h-8 hover:opacity-80 transition-opacity"
                  onClick={() => handleFileDownload(
                    result.file_xml_url, 'xml', result.get_document_type_display, 
                    result.id, result.document_number
                  )}
                  title="Descargar XML"
                >
                  <img src={require("../assets/img/xml.png")} alt="XML" className="w-full h-full object-contain" />
                </button>
                <button
                  className="w-8 h-8 hover:opacity-80 transition-opacity"
                  onClick={() => handleFileDownload(
                    result.file_ride_url, 'pdf', result.get_document_type_display, 
                    result.id, result.document_number
                  )}
                  title="Descargar PDF"
                >
                  <img src={require("../assets/img/pdf.png")} alt="PDF" className="w-full h-full object-contain" />
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500 text-xs">Identificación:</span>
                <p className="font-medium text-gray-900">{result.vat}</p>
              </div>
              <div>
                <span className="text-gray-500 text-xs">Valor:</span>
                <p className="font-semibold text-green-600">$ {result.amount.toFixed(2)}</p>
              </div>
              <div className="col-span-2">
                <span className="text-gray-500 text-xs">Empresa:</span>
                <p className="font-medium text-gray-900 truncate">{result.company_name}</p>
              </div>
              <div className="col-span-2">
                <span className="text-gray-500 text-xs">Fecha de emisión:</span>
                <p className="text-gray-900">
                  {new Date(result.emission_date).toLocaleDateString("es-ES", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TableResultsComponent;
